import { useEffect } from 'react';

/**
 * Niezawodne zarządzanie tagami SEO przez bezpośrednią manipulację <head>.
 *
 * react-helmet-async bywa zawodny w naszej konfiguracji (ustawia <title>, ale
 * gubi <meta>/<link canonical> lub duplikuje canonical przy nawigacji SPA).
 * Ten hook wstrzykuje tagi w useEffect (wzorzec sprawdzony na /firma) i czyści
 * je przy odmontowaniu. Obsługuje wiele bloków Schema.org JSON-LD.
 */
export function usePageSEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  ogSiteName,
  ogImageWidth,
  ogImageHeight,
  twitterCard = 'summary_large_image',
  twitterImage,
  robots,
  geoRegion,
  geoPlacename,
  author,
  schemas,
}) {
  const schemasKey = JSON.stringify(schemas || null);
  useEffect(() => {
    if (!title) return;
    document.title = title;

    const setMeta = (attr, attrVal, content) => {
      if (content === undefined || content === null) return;
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', String(content));
      el.setAttribute('data-pb', 'true');
    };

    setMeta('name', 'description', description || '');
    setMeta('property', 'og:title', ogTitle || title);
    setMeta('property', 'og:description', ogDescription || description || '');
    setMeta('property', 'og:type', ogType || 'website');
    if (canonical) setMeta('property', 'og:url', canonical);
    if (ogSiteName) setMeta('property', 'og:site_name', ogSiteName);
    if (ogImage) {
      setMeta('property', 'og:image', ogImage);
      if (ogImageWidth) setMeta('property', 'og:image:width', ogImageWidth);
      if (ogImageHeight) setMeta('property', 'og:image:height', ogImageHeight);
      if (ogImageAlt) setMeta('property', 'og:image:alt', ogImageAlt);
    }
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', ogTitle || title);
    setMeta('name', 'twitter:description', ogDescription || description || '');
    if (twitterImage || ogImage) setMeta('name', 'twitter:image', twitterImage || ogImage);
    if ((twitterImage || ogImage) && ogImageAlt) setMeta('name', 'twitter:image:alt', ogImageAlt);
    if (robots) setMeta('name', 'robots', robots);
    if (geoRegion) setMeta('name', 'geo.region', geoRegion);
    if (geoPlacename) setMeta('name', 'geo.placename', geoPlacename);
    if (author) setMeta('name', 'author', author);

    // Canonical (jeden, własny — index.html nie ma statycznego canonical)
    let link = document.querySelector('link[rel="canonical"][data-pb]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      link.setAttribute('data-pb', 'true');
      document.head.appendChild(link);
    }
    link.href = canonical || window.location.href;

    // Schema.org JSON-LD (wiele bloków: Product/LocalBusiness + Breadcrumb + FAQ ...)
    document.querySelectorAll('script[data-pb="schema"]').forEach((el) => el.remove());
    const list = Array.isArray(schemas) ? schemas : schemas ? [schemas] : [];
    list.forEach((obj) => {
      if (!obj) return;
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-pb', 'schema');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.querySelectorAll('[data-pb]').forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title, description, canonical, ogTitle, ogDescription, ogType, ogImage, ogImageAlt, ogSiteName,
    ogImageWidth, ogImageHeight, twitterCard, twitterImage, robots, geoRegion, geoPlacename,
    author, schemasKey,
  ]);
}

/**
 * Komponent-opakowanie na usePageSEO. Renderuje null — przydatny gdy chcemy
 * wywołać SEO w JSX (np. po wczesnych returnach loading/error, gdzie hook
 * nie może być wywołany warunkowo w komponencie-rodzicu).
 */
export function SeoHead(props) {
  usePageSEO(props);
  return null;
}

export default usePageSEO;
